import React, { useState, useEffect } from "react";
import { Table, Button, Form, message, Modal } from "antd";
import API from "api/endpoints";
import moment from "moment";
import axiosInstance from "api/axiosInstance";

const useSoftwareAssets = () => {
  const [softwareAssets, setSoftwareAssets] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [isAdding, setIsAdding] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [form] = Form.useForm();
  const [departments, setDepartments] = useState([]);

  useEffect(() => {
    fetchSoftwareAssets();
    fetchDepartments();
  }, []);

  const fetchDepartments = async () => {
    try {
      const res = await axiosInstance.get(API.department.getList);
      setDepartments(res.data);
    } catch (error) {
      message.error("failed to laod department");
    }
  };

  const fetchEmployeesByDepartment = async (departmentId) => {
    try {
      const res = await axiosInstance.get(API.employee.getAllOverview(departmentId));
      setEmployees(res.data);
    } catch (error) {
      message.error("Failed to load employees");
    }
  };

  // Fetch all software assets
  const fetchSoftwareAssets = async () => {
    try {
      const response = await axiosInstance.get(API.softwareAssets.getAll);
      const assets = response.data;
      setSoftwareAssets(assets);
    } catch (error) {
      console.error("❌ Error fetching software assets:", error);
      message.error("Failed to fetch software assets.");
    }
  };

  const toggleAddForm = () => {
    setIsAdding(!isAdding);
    form.resetFields();
  };

  const handleAddSoftwareAsset = async (values) => {
    try {
      setIsLoading(true);

      const formattedValues = {
        employeeId: values.employeeId,
        name: values.name,
        category: values.category,
        assetsCost: values.assetsCost,
        notes: values.notes,
        description: values.description,
        provider: values.provider,
        licenseKey: values.licenseKey,
        subscriptionDate: values.subscriptionDate.format("YYYY-MM-DD"),
        expiryDate: values.expiryDate.format("YYYY-MM-DD"),
      };

      await axiosInstance.post(API.softwareAssets.add, formattedValues);
      message.success("Software asset added successfully.");
      toggleAddForm();
      fetchSoftwareAssets();
    } catch (error) {
      console.error("❌ Error adding software asset:", error);
      message.error("Failed to add software asset.");
    } finally {
      setIsLoading(false);
    }
  };

  const columns = [
    { title: "Employee Id", dataIndex: "employeeId", key: "employeeId" },
    { title: "Name", dataIndex: "name", key: "name" },
    { title: "Department", dataIndex: "category", key: "category" },
    {
      title: "Assets Cost",
      dataIndex: "assetsCost",
      key: "assetsCost",
      render: (cost) => `$${cost.toFixed(2)}`,
    },
    {
      title: "Action",
      key: "action",
      render: (_, record) => (
        <Button type="link" onClick={() => handleViewMore(record)}>
          View More
        </Button>
      ),
    },
  ];

  const additionalColumns = [
    { title: "License Key", dataIndex: "licenseKey", key: "licenseKey" },
    {
      title: "Subscription Date",
      dataIndex: "subscriptionDate",
      key: "subscriptionDate",
      render: (date) => moment(date).format("MMM-DD-yyyy"),
    },
    {
      title: "Expiry Date",
      dataIndex: "expiryDate",
      key: "expiryDate",
      render: (date) => moment(date).format("MMM-DD-yyyy"),
    },
    { title: "Notes", dataIndex: "notes", key: "notes" },
    { title: "Description", dataIndex: "description", key: "description" },
  ];

  const handleViewMore = (asset) => {
    Modal.info({
      title: "Additional Data",
      content: (
        <div>
          <Table
            dataSource={[asset]}
            columns={additionalColumns}
            className="custom-table"
            pagination={false}
            rowKey="licenseKey"
          />
        </div>
      ),
      centered: true,
      width: "800px",
    });
  };

  const disablePastDates = (current) => current && current < moment().startOf("day");
  const disableExpiryDate = (current) => {
    const subscriptionDate = form.getFieldValue("subscriptionDate");
    return (
      current && (current.isSame(subscriptionDate, "day") || current.isBefore(subscriptionDate))
    );
  };

  return {
    softwareAssets,

    isLoading,
    handleAddSoftwareAsset,
    columns,
    disablePastDates,
    disableExpiryDate,
    isAdding,
    toggleAddForm,
    form,
    departments,
    employees,
    fetchEmployeesByDepartment,
  };
};

export default useSoftwareAssets;
